/**
 * Keycloak Management Tab
 * Keycloak 管理 - OIDC 登录 + 用户/组同步状态
 */

import { useEffect, useState, useCallback } from 'react';
import {
  InfoCard,
  StructuredMetadataTable,
} from '@backstage/core-components';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import RefreshIcon from '@material-ui/icons/Refresh';
import PeopleIcon from '@material-ui/icons/People';
import GroupIcon from '@material-ui/icons/Group';
import LockIcon from '@material-ui/icons/Lock';
import SyncIcon from '@material-ui/icons/Sync';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import SearchIcon from '@material-ui/icons/Search';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  statCard: {
    padding: theme.spacing(2.5),
    textAlign: 'center',
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1,
  },
  statusBanner: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  success: {
    background: 'rgba(76, 175, 80, 0.08)',
    border: '1px solid rgba(76, 175, 80, 0.3)',
  },
  error: {
    background: 'rgba(244, 67, 54, 0.08)',
    border: '1px solid rgba(244, 67, 54, 0.3)',
  },
  scopeChip: {
    margin: 2,
    fontSize: '0.7rem',
    fontFamily: 'monospace',
  },
}));

interface KeycloakCheckResult {
  oidcConfigured: boolean;
  oidcConfig?: { metadataUrl: string; clientId: string; callbackUrl?: string };
  oidcReachable?: boolean;
  oidcError?: string;
  oidcDiscovery?: {
    issuer: string;
    authorization_endpoint: string;
    token_endpoint: string;
    userinfo_endpoint: string;
    end_session_endpoint?: string;
    grant_types_supported?: string[];
    scopes_supported?: string[];
  };
  orgSyncConfigured: boolean;
  orgSyncConfig?: {
    baseUrl: string;
    realm: string;
    loginRealm?: string;
    clientId: string;
    schedule?: { frequency: string; timeout: string };
    userQuerySize?: number;
    groupQuerySize?: number;
  };
  orgSyncReachable?: boolean;
  orgSyncError?: string;
  realmInfo?: {
    realm: string;
    displayName?: string;
    enabled?: boolean;
    registrationAllowed?: boolean;
    resetPasswordAllowed?: boolean;
  };
  userCount?: number;
  groupCount?: number;
  groups?: Array<{
    id: string;
    name: string;
    path: string;
    memberCount: number;
    subGroupCount: number;
  }>;
  checkedAt: string;
}

export const KeycloakManagementTab: React.FC = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const [result, setResult] = useState<KeycloakCheckResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [groupsExpanded, setGroupsExpanded] = useState(false);
  const [groupSearch, setGroupSearch] = useState('');
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [groupMembers, setGroupMembers] = useState<Record<string, any[]>>({});
  const [loadingGroupId, setLoadingGroupId] = useState<string | null>(null);

  const check = useCallback(async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();
      const resp = await fetch(
        `${baseUrl}/external-services/keycloak/check${force ? '?refresh=true' : ''}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setResult(await resp.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, identityApi]);

  useEffect(() => { check(); }, [check]);

  const fetchGroupMembers = useCallback(async (groupId: string) => {
    if (groupMembers[groupId]) return; // 已缓存
    setLoadingGroupId(groupId);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();
      const resp = await fetch(
        `${baseUrl}/external-services/keycloak/groups/${groupId}/members`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (resp.ok) {
        const data = await resp.json();
        setGroupMembers(prev => ({ ...prev, [groupId]: data.members || [] }));
      }
    } catch { /* ignore */ }
    setLoadingGroupId(null);
  }, [discoveryApi, identityApi, groupMembers]);

  if (loading && !result) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>}

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          size="small"
          variant="outlined"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={() => check(true)}
          disabled={loading}
          style={{ textTransform: 'none' }}
        >
          {loading ? '检测中...' : '重新检测'}
        </Button>
      </Box>

      {result && (
        <>
          {/* 统计卡片 */}
          <Grid container spacing={2} style={{ marginBottom: 24 }}>
            <Grid item xs={12} sm={3}>
              <Paper className={classes.statCard} variant="outlined">
                <LockIcon style={{ fontSize: 28, color: result.oidcReachable ? '#4caf50' : '#f44336' }} />
                <Typography variant="caption" color="textSecondary">OIDC 登录</Typography>
                <Typography style={{ fontWeight: 600, color: result.oidcReachable ? '#4caf50' : '#f44336' }}>
                  {result.oidcReachable ? '正常' : result.oidcConfigured ? '异常' : '未配置'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper className={classes.statCard} variant="outlined">
                <SyncIcon style={{ fontSize: 28, color: result.orgSyncReachable ? '#4caf50' : '#f44336' }} />
                <Typography variant="caption" color="textSecondary">用户组同步</Typography>
                <Typography style={{ fontWeight: 600, color: result.orgSyncReachable ? '#4caf50' : '#f44336' }}>
                  {result.orgSyncReachable ? '正常' : result.orgSyncConfigured ? '异常' : '未配置'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper className={classes.statCard} variant="outlined">
                <PeopleIcon style={{ fontSize: 28, color: '#1976d2' }} />
                <Typography variant="caption" color="textSecondary">用户数量</Typography>
                <Typography className={classes.statNumber} style={{ color: '#1976d2' }}>
                  {result.userCount ?? '-'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper className={classes.statCard} variant="outlined">
                <GroupIcon style={{ fontSize: 28, color: '#7b1fa2' }} />
                <Typography variant="caption" color="textSecondary">组数量</Typography>
                <Typography className={classes.statNumber} style={{ color: '#7b1fa2' }}>
                  {result.groupCount ?? '-'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* OIDC 配置 */}
          <InfoCard title="OIDC 登录认证">
            <Box className={`${classes.statusBanner} ${result.oidcReachable ? classes.success : classes.error}`}>
              {result.oidcReachable ? (
                <CheckCircleIcon style={{ color: '#4caf50' }} />
              ) : (
                <ErrorIcon style={{ color: '#f44336' }} />
              )}
              <Box>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  {result.oidcReachable ? 'OIDC Discovery 端点可达' : result.oidcError || 'OIDC 不可用'}
                </Typography>
                {result.oidcConfig && (
                  <Typography variant="caption" color="textSecondary">
                    Client: {result.oidcConfig.clientId}
                  </Typography>
                )}
              </Box>
            </Box>

            {result.oidcConfig && (
              <StructuredMetadataTable metadata={{
                'Metadata URL': result.oidcConfig.metadataUrl,
                'Client ID': result.oidcConfig.clientId,
                'Callback URL': result.oidcConfig.callbackUrl || '-',
                ...(result.oidcDiscovery ? {
                  'Issuer': result.oidcDiscovery.issuer,
                  'Auth Endpoint': result.oidcDiscovery.authorization_endpoint,
                  'Token Endpoint': result.oidcDiscovery.token_endpoint,
                  'UserInfo Endpoint': result.oidcDiscovery.userinfo_endpoint,
                  'Logout Endpoint': result.oidcDiscovery.end_session_endpoint || '-',
                } : {}),
              }} />
            )}

            {result.oidcDiscovery?.scopes_supported && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                  支持的 Scopes
                </Typography>
                <Box display="flex" flexWrap="wrap">
                  {result.oidcDiscovery.scopes_supported.map(s => (
                    <Chip key={s} label={s} size="small" className={classes.scopeChip} />
                  ))}
                </Box>
              </Box>
            )}

            {result.oidcDiscovery?.grant_types_supported && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                  支持的 Grant Types
                </Typography>
                <Box display="flex" flexWrap="wrap">
                  {result.oidcDiscovery.grant_types_supported.map(g => (
                    <Chip key={g} label={g} size="small" className={classes.scopeChip} color="primary" />
                  ))}
                </Box>
              </Box>
            )}
          </InfoCard>

          {/* 用户组同步 */}
          <Box mt={2}>
            <InfoCard title="Catalog 用户/组同步 (keycloakOrg)">
              <Box className={`${classes.statusBanner} ${result.orgSyncReachable ? classes.success : classes.error}`}>
                {result.orgSyncReachable ? (
                  <CheckCircleIcon style={{ color: '#4caf50' }} />
                ) : (
                  <ErrorIcon style={{ color: '#f44336' }} />
                )}
                <Box>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    {result.orgSyncReachable ? 'Keycloak Admin API 连通' : result.orgSyncError || '连接失败'}
                  </Typography>
                </Box>
              </Box>

              {result.orgSyncConfig && (
                <StructuredMetadataTable metadata={{
                  'Base URL': result.orgSyncConfig.baseUrl,
                  'Realm': result.orgSyncConfig.realm,
                  'Login Realm': result.orgSyncConfig.loginRealm || result.orgSyncConfig.realm,
                  'Client ID': result.orgSyncConfig.clientId,
                  '同步频率': result.orgSyncConfig.schedule?.frequency || '-',
                  '超时时间': result.orgSyncConfig.schedule?.timeout || '-',
                  'User Query Size': String(result.orgSyncConfig.userQuerySize || '-'),
                  'Group Query Size': String(result.orgSyncConfig.groupQuerySize || '-'),
                }} />
              )}

              {result.realmInfo && (
                <Box mt={2}>
                  <Divider style={{ marginBottom: 16 }} />
                  <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                    Realm 信息
                  </Typography>
                  <StructuredMetadataTable metadata={{
                    'Realm': result.realmInfo.realm,
                    '显示名': result.realmInfo.displayName || '-',
                    '状态': result.realmInfo.enabled ? '启用' : '禁用',
                    '允许注册': result.realmInfo.registrationAllowed ? '是' : '否',
                    '允许重置密码': result.realmInfo.resetPasswordAllowed ? '是' : '否',
                  }} />
                </Box>
              )}
            </InfoCard>
          </Box>

          {/* 用户组列表 */}
          {result.groups && result.groups.length > 0 && (
            <Box mt={2}>
              <Accordion
                expanded={groupsExpanded}
                onChange={() => setGroupsExpanded(!groupsExpanded)}
                variant="outlined"
                style={{ borderRadius: 8 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <GroupIcon style={{ color: '#7b1fa2' }} />
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                      用户组列表 ({result.groups.length})
                    </Typography>
                    <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                      点击组名查看成员
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails style={{ flexDirection: 'column', padding: '0 16px 16px' }}>
                  <Box mb={2}>
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder="搜索组名..."
                      value={groupSearch}
                      onChange={e => setGroupSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon style={{ fontSize: 18, color: '#999' }} />
                          </InputAdornment>
                        ),
                      }}
                      style={{ width: 300 }}
                    />
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ width: 40 }} />
                          <TableCell style={{ fontWeight: 600 }}>组名</TableCell>
                          <TableCell style={{ fontWeight: 600 }}>路径</TableCell>
                          <TableCell style={{ fontWeight: 600 }} align="center">子组数</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result.groups
                          .filter(g =>
                            !groupSearch ||
                            g.name.toLowerCase().includes(groupSearch.toLowerCase()) ||
                            g.path.toLowerCase().includes(groupSearch.toLowerCase())
                          )
                          .map(group => {
                            const isExpanded = expandedGroupId === group.id;
                            const members = groupMembers[group.id];
                            const isLoading = loadingGroupId === group.id;
                            return [
                              <TableRow
                                key={group.id}
                                hover
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  if (isExpanded) {
                                    setExpandedGroupId(null);
                                  } else {
                                    setExpandedGroupId(group.id);
                                    fetchGroupMembers(group.id);
                                  }
                                }}
                              >
                                <TableCell style={{ width: 40, padding: '4px 8px' }}>
                                  <IconButton size="small">
                                    {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                                  </IconButton>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                                    {group.name}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption" style={{ fontFamily: 'monospace', color: '#666' }}>
                                    {group.path}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  {group.subGroupCount > 0 ? (
                                    <Chip label={group.subGroupCount} size="small" variant="outlined" style={{ minWidth: 32 }} />
                                  ) : (
                                    <Typography variant="caption" color="textSecondary">—</Typography>
                                  )}
                                </TableCell>
                              </TableRow>,
                              <TableRow key={`${group.id}-detail`}>
                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
                                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Box py={2} pl={4}>
                                      {isLoading ? (
                                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                          <CircularProgress size={16} />
                                          <Typography variant="body2" color="textSecondary">加载成员中...</Typography>
                                        </Box>
                                      ) : members && members.length > 0 ? (
                                        <>
                                          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                                            成员 ({members.length})
                                          </Typography>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell style={{ fontWeight: 600, fontSize: '0.75rem' }}>用户名</TableCell>
                                                <TableCell style={{ fontWeight: 600, fontSize: '0.75rem' }}>姓名</TableCell>
                                                <TableCell style={{ fontWeight: 600, fontSize: '0.75rem' }}>邮箱</TableCell>
                                                <TableCell style={{ fontWeight: 600, fontSize: '0.75rem' }} align="center">状态</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {members.map((m: any) => (
                                                <TableRow key={m.id}>
                                                  <TableCell>
                                                    <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                      {m.username}
                                                    </Typography>
                                                  </TableCell>
                                                  <TableCell>
                                                    <Typography variant="body2" style={{ fontSize: '0.8rem' }}>
                                                      {[m.lastName, m.firstName].filter(Boolean).join('') || '-'}
                                                    </Typography>
                                                  </TableCell>
                                                  <TableCell>
                                                    <Typography variant="body2" style={{ fontSize: '0.8rem', color: '#666' }}>
                                                      {m.email || '-'}
                                                    </Typography>
                                                  </TableCell>
                                                  <TableCell align="center">
                                                    <Chip
                                                      label={m.enabled ? '启用' : '禁用'}
                                                      size="small"
                                                      style={{
                                                        fontSize: '0.7rem',
                                                        backgroundColor: m.enabled ? 'rgba(76,175,80,0.1)' : 'rgba(244,67,54,0.1)',
                                                        color: m.enabled ? '#4caf50' : '#f44336',
                                                      }}
                                                    />
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </>
                                      ) : members ? (
                                        <Typography variant="body2" color="textSecondary">该组暂无成员</Typography>
                                      ) : null}
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>,
                            ];
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}

          {/* 检测时间 */}
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Typography variant="caption" color="textSecondary">
              检测时间: {new Date(result.checkedAt).toLocaleString('zh-CN')}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};
